import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { fuzzyMatchAnswer } from './fuzzyMatch';

const isDev = process.env.NODE_ENV === 'development';

/**
 * FillBlankQuestion Component
 * 
 * Renders a question with a visible input field replacing the ___ pattern.
 * User can type their answer directly.
 * Uses fuzzy matching for forgiving grading (85% similarity threshold).
 */
export default function FillBlankQuestion({
  question,
  userAnswer = '',
  onAnswerChange,
  showResult = false,
  correctAnswer = '',
  disabled = false
}) {
  const questionText = question?.question_text || '';
  
  // Calculate fuzzy match result for display purposes
  const matchResult = useMemo(() => {
    if (!showResult || !userAnswer.trim()) return null;
    return fuzzyMatchAnswer(userAnswer, correctAnswer, { threshold: 0.85 });
  }, [showResult, userAnswer, correctAnswer]);
  
  // Split the question text around the blank pattern
  const renderQuestionWithInput = () => {
    // Find the blank pattern (___, [blank], {blank})
    const blankPattern = /_{3,}|\[blank\]|\{blank\}/gi;
    const parts = questionText.split(blankPattern);
    
    if (isDev) console.log(`[TEI_RENDERER] fill_blank rendering: parts=${parts.length}, userAnswer="${userAnswer}"`);
    
    if (parts.length < 2) {
      // No blank found - just show input below question
      return (
        <div>
          <p className="text-lg text-sage-900 mb-4">{questionText}</p>
          {renderInputField()}
        </div>
      );
    }
    
    // Render question with inline input
    return (
      <div className="text-lg text-sage-900 leading-relaxed">
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            <span>{part}</span>
            {index < parts.length - 1 && (
              <span className="inline-block mx-1 align-middle">
                {renderInputField(true)}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };
  
  const renderInputField = (inline = false) => {
    // Use fuzzy match result for display
    const isCorrect = showResult && matchResult?.isCorrect;
    const isIncorrect = showResult && !matchResult?.isCorrect;
    const similarityPercent = matchResult ? Math.round(matchResult.similarity * 100) : 0;
    
    let inputClass = inline 
      ? "inline-block w-48 h-10 px-3 text-base border-2 rounded-lg"
      : "w-full h-12 px-4 text-lg border-2 rounded-xl";
    
    if (showResult) {
      if (isCorrect) {
        inputClass += " border-green-500 bg-green-50";
      } else {
        inputClass += " border-red-500 bg-red-50";
      }
    } else {
      inputClass += " border-border focus:border-sage-500";
    }
    
    return (
      <div className={inline ? "inline-flex items-center gap-2" : "mt-4 flex items-center gap-3"}>
        <Input
          type="text"
          value={userAnswer}
          onChange={(e) => {
            if (!disabled && !showResult) {
              if (isDev) console.log(`[TEI_RENDERER] fill_blank input change: "${e.target.value}"`);
              onAnswerChange(e.target.value);
            }
          }}
          disabled={disabled || showResult}
          className={inputClass}
          placeholder="Type your answer..."
          data-testid="fill-blank-input"
        />
        {showResult && (
          isCorrect 
            ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
        )}
      </div>
    );
  };
  
  return (
    <div data-testid="fill-blank-question">
      {renderQuestionWithInput()}
      
      {/* Show correct answer for incorrect answers */}
      {showResult && !matchResult?.isCorrect && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-700">
            <span className="font-medium">Correct answer:</span> {correctAnswer}
          </p>
        </div>
      )}
      
      {/* Show simple "Close enough!" message for accepted fuzzy matches (no percentage shown) */}
      {showResult && matchResult?.isCorrect && matchResult.similarity < 1 && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
          <p className="text-sm text-green-700">
            Close enough! Your answer was accepted.
          </p>
        </div>
      )}
    </div>
  );
}
